/**
 * DeviationList — system-generated deviations (read-only list, no create).
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { useDeviations } from '../../hooks/useDeviations';
import DeviationDetail from './DeviationDetail';
import './DeviationList.css';

const ALLOWED_ROLES = ['management', 'ic_personnel', 'administration', 'admin', 'Management', 'IC_Personnel', 'Administration', 'Admin'];

const STATUS_BADGE = {
  open: 'badge-gray',
  under_investigation: 'badge-amber',
  resolved: 'badge-teal'
};

const SEVERITY_BADGE = {
  critical: 'badge-red',
  major: 'badge-coral',
  minor: 'badge-amber'
};

const STATUS_LABEL_KEY = {
  open: 'statusOpen',
  under_investigation: 'statusUnderInvestigation',
  resolved: 'statusResolved'
};

const SEVERITY_LABEL_KEY = {
  critical: 'severityCritical',
  major: 'severityMajor',
  minor: 'severityMinor'
};

const DeviationList = ({ organizationId, userId, userRole }) => {
  const { t, i18n } = useTranslation('deviations');
  const navigate = useNavigate();
  const { items, loading, error, filters, setFilters, refetch } = useDeviations(organizationId);
  const [selectedDeviationId, setSelectedDeviationId] = useState(null);

  useEffect(() => {
    if (!userRole) return;
    if (!ALLOWED_ROLES.includes(userRole)) navigate('/', { replace: true });
  }, [userRole, navigate]);

  if (!organizationId) {
    return (
      <div className="deviation-list">
        <p className="deviation-empty">{t('noOrganization')}</p>
      </div>
    );
  }

  const fmtDate = (ts) => {
    if (!ts) return t('dateUnknown');
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString(i18n.language === 'ro' ? 'ro-RO' : 'en-GB');
    } catch (_) {
      return t('dateUnknown');
    }
  };

  return (
    <div className="deviation-list">
      <div className="deviation-list-header">
        <h2>{t('title')}</h2>
        <button type="button" className="btn-icon" onClick={refetch} title={t('refresh')} aria-label={t('refresh')}>
          <RefreshCw size={16} />
        </button>
      </div>

      <div className="deviation-list-filters">
        <select
          value={filters.status || 'all'}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="all">{t('filterAll')}</option>
          <option value="open">{t('filterOpen')}</option>
          <option value="under_investigation">{t('filterUnderInvestigation')}</option>
          <option value="resolved">{t('filterResolved')}</option>
        </select>
        <select
          value={filters.severity || 'all'}
          onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
        >
          <option value="all">{t('filterAll')}</option>
          <option value="critical">{t('severityCritical')}</option>
          <option value="major">{t('severityMajor')}</option>
          <option value="minor">{t('severityMinor')}</option>
        </select>
        <label className="filter-checkbox">
          <input
            type="checkbox"
            checked={!!filters.isRecurring}
            onChange={(e) => setFilters({ ...filters, isRecurring: e.target.checked })}
          />
          {t('filterRecurring')}
        </label>
      </div>

      {error && (
        <div className="deviation-error">
          <AlertTriangle size={16} /> {error.message || t('errorLoading')}
        </div>
      )}
      {loading && <div className="deviation-loading">{t('loading')}</div>}

      {!loading && items.length === 0 && (
        <div className="deviation-empty-card">
          <h3 className="deviation-empty-title">{t('emptyStateTitle')}</h3>
          <p className="deviation-empty-muted">{t('noItems')}</p>
          <p className="deviation-empty-hint">{t('emptyStateHint')}</p>
        </div>
      )}

      <div className="deviation-items">
        {items.map((dev) => (
          <div
            key={dev.id}
            className="deviation-card"
            onClick={() => setSelectedDeviationId(dev.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setSelectedDeviationId(dev.id)}
          >
            <div className="deviation-card-header">
              <span className="deviation-code">{dev.deviation_code || dev.id.slice(0, 12)}</span>
              <div className="deviation-badges">
                <span
                  className={`deviation-badge ${SEVERITY_BADGE[dev.severity] || 'badge-gray'}`}
                >
                  {dev.severity ? t(SEVERITY_LABEL_KEY[dev.severity] || 'severityUnknown') : t('severityUnknown')}
                </span>
                <span className={`deviation-badge ${STATUS_BADGE[dev.status] || 'badge-gray'}`}>
                  {dev.status ? t(STATUS_LABEL_KEY[dev.status] || 'statusOpen') : t('statusOpen')}
                </span>
                {dev.isRecurring && (
                  <span className="deviation-badge badge-recurring">
                    <AlertTriangle size={11} /> {t('recurring')} ({dev.occurrenceCount ?? '—'})
                  </span>
                )}
              </div>
            </div>
            <div className="deviation-card-body">
              <span className="deviation-location">
                {dev.locationPath ||
                  dev.entityId ||
                  (dev.entityType ? `${dev.entityType}: ${dev.entityId || ''}` : t('unknownLocation'))}
              </span>
              {dev.protocolName ? (
                <span className="deviation-protocol">{dev.protocolName}</span>
              ) : (
                <span className="deviation-protocol">
                  {dev.protocolId ? t('protocolFallback', { id: dev.protocolId }) : '—'}
                </span>
              )}
              {dev.biologicalEntities?.length > 0 && (
                <span className="deviation-bio">{dev.biologicalEntities.join(' · ')}</span>
              )}
              {dev.rpnAtTime > 0 && <span className="deviation-rpn">RPN {dev.rpnAtTime}</span>}
            </div>
            <div className="deviation-card-footer">
              {dev.assignedToName && <span className="deviation-executor">{dev.assignedToName}</span>}
              <span className="deviation-date">{fmtDate(dev.createdAt)}</span>
            </div>
          </div>
        ))}
      </div>

      {selectedDeviationId && (
        <DeviationDetail
          organizationId={organizationId}
          userId={userId}
          userRole={userRole}
          deviationId={selectedDeviationId}
          onClose={() => {
            setSelectedDeviationId(null);
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default DeviationList;
